import React, { useEffect, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
  Circle,
} from "@react-google-maps/api";
import { useMediaQuery } from "@mui/material";
import "./PropertyMaps.css";


// --------------------------------------------------
// Price formatter
// --------------------------------------------------
const formatPrice = (price) => {
  if (
    price === null ||
    price === undefined ||
    price === "" ||
    Number.isNaN(Number(price))
  ) {
    return "Price not available";
  }

  const value = Number(price);

  if (value >= 10000000) {
    return `₹ ${(value / 10000000).toFixed(2)} Cr`;
  }

  if (value >= 100000) {
    return `₹ ${(value / 100000).toFixed(2)} Lakh`;
  }

  return `₹ ${value.toLocaleString("en-IN")}`;
};


// --------------------------------------------------
// Property Map
// --------------------------------------------------
const PropertyMaps = ({
  properties = [],
  center,
  zoom = 12,
}) => {
  const mapRef = useRef(null);

  const isMobile = useMediaQuery("(max-width:768px)");

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [selected, setSelected] = React.useState(null);
  const [isFullscreen, setIsFullscreen] =
    React.useState(false);

  const containerStyle = {
    width: "100%",
    height: isFullscreen
      ? "100vh"
      : isMobile
      ? "300px"
      : "200px",
  };


  // --------------------------------------------------
  // Group properties using same coordinates
  // --------------------------------------------------
  const groupedProperties = properties.reduce(
    (acc, property) => {
      const lat = Number(property.map_latitude);
      const lng = Number(property.map_longitude);

      if (
        Number.isNaN(lat) ||
        Number.isNaN(lng)
      ) {
        console.log(
          "Invalid map coordinates____",
          property
        );

        return acc;
      }

      const key = `${lat},${lng}`;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(property);

      return acc;
    },
    {}
  );


  // --------------------------------------------------
  // Google Map loaded
  // --------------------------------------------------
  const handleMapLoad = (map) => {
    mapRef.current = map;
  };


  // --------------------------------------------------
  // Fit map to search center + properties
  // --------------------------------------------------
  useEffect(() => {
    if (
      !mapRef.current ||
      !window.google ||
      !center ||
      !properties.length
    ) {
      return;
    }

    const bounds =
      new window.google.maps.LatLngBounds();

    // Search location
    bounds.extend({
      lat: Number(center.lat),
      lng: Number(center.lng),
    });

    // Property locations
    properties.forEach((property) => {
      const lat = Number(
        property.map_latitude
      );

      const lng = Number(
        property.map_longitude
      );

      if (
        !Number.isNaN(lat) &&
        !Number.isNaN(lng)
      ) {
        bounds.extend({
          lat,
          lng,
        });
      }
    });

    mapRef.current.fitBounds(bounds);
  }, [properties, center]);


  // --------------------------------------------------
  // Debug
  // --------------------------------------------------
  console.log(
    "MAP CENTER____",
    center
  );

  console.log(
    "MAP PROPERTIES____",
    properties
  );

  console.log(
    "GROUPED PROPERTIES____",
    groupedProperties
  );


  if (!isLoaded) {
    return <p>Loading map...</p>;
  }

  if (!center) {
    return <p>Loading map location...</p>;
  }


  return (
    <div
      className={`map-container ${
        isFullscreen ? "fullscreen" : ""
      }`}
    >

      {/* -------------------------------------------- */}
      {/* Fullscreen button */}
      {/* -------------------------------------------- */}
      <button
        className="fullscreen-toggle"
        onClick={() =>
          setIsFullscreen(!isFullscreen)
        }
      >
        {isFullscreen
          ? "Exit Fullscreen"
          : "Full Map View"}
      </button>


      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        onLoad={handleMapLoad}
      >

        {/* ------------------------------------------ */}
        {/* 15 KM Radius */}
        {/* ------------------------------------------ */}
        <Circle
          center={center}
          radius={15000}
          options={{
            fillColor: "#1976d2",
            fillOpacity: 0.1,
            strokeColor: "#1976d2",
            strokeOpacity: 0.8,
            strokeWeight: 2,
          }}
        />


        {/* ------------------------------------------ */}
        {/* Search Location Marker */}
        {/* ------------------------------------------ */}
        <Marker
          position={center}
          icon={{
            url:
              "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
          }}
          title="Search Location"
        />


        {/* ------------------------------------------ */}
        {/* Property Markers */}
        {/* ------------------------------------------ */}
        {Object.entries(
          groupedProperties
        ).map(([key, group]) => {

          const [lat, lng] = key
            .split(",")
            .map(Number);

          return (
            <Marker
              key={key}
              position={{
                lat,
                lng,
              }}

              // ------------------------------------
              // SHOW POPUP ON HOVER
              // ------------------------------------
              onMouseOver={() => {
                setSelected({
                  lat,
                  lng,
                  properties: group,
                });
              }}

              title={
                group.length > 1
                  ? `${group.length} Properties`
                  : group[0]?.ltg_title ||
                    "Property"
              }
            />
          );
        })}


        {/* ------------------------------------------ */}
        {/* Property Info Window */}
        {/* ------------------------------------------ */}
        {selected && (
          <InfoWindow
            position={{
              lat: selected.lat,
              lng: selected.lng,
            }}
            onCloseClick={() =>
              setSelected(null)
            }
          >

            <div
              style={{
                width: "280px",
                maxWidth: "100%",
                padding: "4px",
                fontFamily:
                  "Arial, sans-serif",
              }}
            >

              {/* Popup Header */}
              <div
                style={{
                  marginBottom: "10px",
                  paddingBottom: "8px",
                  borderBottom:
                    "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1f2937",
                  }}
                >
                  {selected.properties.length ===
                  1
                    ? "Property Details"
                    : `${selected.properties.length} Properties`}
                </div>
              </div>


              {/* ------------------------------------ */}
              {/* Properties */}
              {/* ------------------------------------ */}
              {selected.properties.map(
                (property, index) => (
                  <div
                    key={property.RowID}
                    style={{
                      paddingBottom:
                        "12px",
                      marginBottom:
                        "12px",
                      borderBottom:
                        index !==
                        selected.properties
                          .length -
                          1
                          ? "1px solid #e5e7eb"
                          : "none",
                    }}
                  >

                    {/* Property Title */}
                    <div
                      style={{
                        fontSize:
                          "14px",
                        fontWeight:
                          "600",
                        lineHeight:
                          "1.4",
                        color:
                          "#111827",
                        marginBottom:
                          "6px",
                      }}
                    >
                      {property.ltg_title}
                    </div>


                    {/* Project */}
                    {property.ltg_projectName && (
                      <div
                        style={{
                          fontSize:
                            "12px",
                          color:
                            "#6b7280",
                          marginBottom:
                            "5px",
                        }}
                      >
                        📍{" "}
                        {
                          property.ltg_projectName
                        }
                      </div>
                    )}


                    {/* Price */}
                    <div
                      style={{
                        fontSize:
                          "15px",
                        fontWeight:
                          "700",
                        color:
                          "#15803d",
                        marginBottom:
                          "9px",
                      }}
                    >
                      {formatPrice(
                        property.map_price
                      )}
                    </div>


                    {/* Visit Property */}
                    <button
                      onClick={() => {
                        window.open(
                          `/Property/${property.propertyUrl}`,
                          "_blank"
                        );
                      }}
                      style={{
                        border: "none",
                        background:
                          "#2563eb",
                        color: "#fff",
                        padding:
                          "7px 12px",
                        borderRadius:
                          "5px",
                        fontSize:
                          "12px",
                        fontWeight:
                          "600",
                        cursor:
                          "pointer",
                      }}
                    >
                      View Property →
                    </button>

                  </div>
                )
              )}

            </div>

          </InfoWindow>
        )}

      </GoogleMap>
    </div>
  );
};

export default PropertyMaps;